import { Box, Tab, Tabs } from '@mui/material';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { a11yProps, CustomTabPanel } from 'utils/MaterialUi/Tab';
import Simulation from './Simulation';
import Result from './Result/Result';
import { useNavigationBlocker } from 'router/NavigationBlockerProvider';
import ExploitationHeader from '../header/exploitation.header';
import './index.style.scss';

export const SimulationIndex = ({ exploitationSelected, setExploitation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [simulationResult, setSimulationResult] = useState(null);
  const [hasSimulationRun, setHasSimulationRun] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [showResultsReady, setShowResultsReady] = useState(false);
  const { blockNavigation } = useNavigationBlocker();

  useEffect(() => {
    blockNavigation(isSimulating);
    return () => blockNavigation(false);
  }, [isSimulating, blockNavigation]);

  useEffect(() => {
    if (!exploitationSelected) {
      if (localStorage.getItem('exploitation')) {
        setExploitation(false, +localStorage.getItem('exploitation'));
      } else {
        toast.info(t('message.selectExploitation'));
        navigate('/exploitation');
      }
    }
  }, []);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('simulation');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        if (parsed.current?.params || parsed.history?.length > 0) {
          setHasSimulationRun(true);
        }
      }
    } catch (error) {
      console.error(t('simulation.errors.loadHistory'), error);
    }
  }, [t]);

  const handleChange = (event, newValue) => {
    if (newValue === 1 && (isSimulating || !hasSimulationRun)) return;

    if (newValue === 1) {
      toast.dismiss();
      setShowResultsReady(false);
    }

    setValue(newValue);
  };

  const handleSimulationStatusChange = useCallback((simulating) => {
    setIsSimulating(simulating);
  }, []);

  const handleSimulationComplete = useCallback((result) => {
    // Keep the original sort for the some nitrogen Array of indexes send by the algo
    setSimulationResult({
      ...result,
      originalSortCultures: structuredClone(result.result.cultures),
    });
    setHasSimulationRun(true);
    setShowResultsReady(true);
  }, []);

  const handleNewSimulation = useCallback(() => {
    setValue(0);
  }, []);

  const handleGoToResults = useCallback(() => {
    toast.dismiss();
    setShowResultsReady(false);
    setValue(1);
  }, []);

  return (
    <div>
      <Box sx={{ width: '100%' }}>
        <Box className="simulation-recap__tab-bar" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange}>
            <Tab label={t('navigation.simulation')} {...a11yProps(0)} />
            <Tab
              label={t('simulation.tabs.results')}
              {...a11yProps(1)}
              disabled={!hasSimulationRun || isSimulating}
              className={showResultsReady ? 'tab--results-ready' : ''}
              sx={{
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.38)',
                  cursor: 'not-allowed',
                },
              }}
            />
          </Tabs>
          <ExploitationHeader />
        </Box>
        <div class="tab--container">
          <CustomTabPanel value={value} index={0}>
            <Simulation
              exploitationSelected={exploitationSelected}
              setExploitation={setExploitation}
              onSimulationComplete={handleSimulationComplete}
              onSimulationStatusChange={handleSimulationStatusChange}
              goToResults={handleGoToResults}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {hasSimulationRun && (
              <Result
                exploitation={exploitationSelected}
                simulationResult={simulationResult}
                onNewSimulation={handleNewSimulation}
              />
            )}
          </CustomTabPanel>
        </div>
      </Box>
    </div>
  );
};

export default SimulationIndex;
